import React from "react";
import { Edit, Trash2, Info, Search, Loader } from "lucide-react";
import SearchableSelect from "./SearchableSelect";

export default function TransactionTable({
  transactions,
  searchTerm,
  sortBy,
  sortOrder,
  loading,
  error,
  totalEntries,
  currentPage,
  maxPages,
  startEntry,
  endEntry,
  itemsPerPage,
  onSearch,
  onSort,
  onEdit,
  onDelete,
  onInfo,
  onToggleCashier,
  showCashierColumn = true,
  onPageChange,
  onItemsPerPageChange,
}) {
  const getStatusColor = (status) => {
    return status === "Aktif"
      ? "bg-green-50 border border-green-200 text-green-700"
      : "bg-red-50 border border-red-200 text-red-700";
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Loading State */}
      {loading && (
        <div className="px-6 py-12 flex items-center justify-center text-gray-500">
          <Loader className="w-5 h-5 animate-spin mr-2" />
          Loading data...
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-24">
                    Aksi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  {showCashierColumn && (
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Tampil di Kasir
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Nama Kas/Rekening
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Tipe Akun
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Metode Pembayaran
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Total Saldo
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={showCashierColumn ? 7 : 6}
                      className="px-6 py-8 text-center text-gray-500">
                      Tidak ada data
                    </td>
                  </tr>
                ) : (
                  transactions.map((tx) => (
                    <tr
                      key={tx.id}
                      className="border-b border-gray-200 hover:bg-gray-50 transition">
                      {/* Action Buttons */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => onEdit && onEdit(tx.id)}
                            disabled={!onEdit}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${!onEdit ? "bg-slate-300 text-slate-500 cursor-not-allowed" : "bg-slate-700 text-white hover:bg-slate-800"}`}
                            title={!onEdit ? "Tidak ada akses" : "Edit"}>
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => tx.name?.toLowerCase() !== "laci kasir" && onDelete && onDelete(tx.id)}
                            disabled={!onDelete || tx.name?.toLowerCase() === "laci kasir"}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${!onDelete || tx.name?.toLowerCase() === "laci kasir" ? "bg-slate-300 text-slate-500 cursor-not-allowed" : "bg-rose-500 text-white hover:bg-rose-600"}`}
                            title={!onDelete ? "Tidak ada akses" : tx.name?.toLowerCase() === "laci kasir" ? "Laci Kasir utama tidak dapat dihapus" : "Hapus"}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => onInfo(tx.id)}
                            className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors"
                            title="Detail">
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 rounded border text-xs font-medium ${getStatusColor(
                            tx.status,
                          )}`}>
                          {tx.status}
                        </span>
                      </td>

                      {/* Tampil di Kasir Switch */}
                      {showCashierColumn && (
                        <td className="px-6 py-4 text-center">
                          {onToggleCashier ? (
                            <button
                              type="button"
                              role="switch"
                              aria-checked={tx.show_to_cashier || false}
                              onClick={() => onToggleCashier(tx)}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                tx.show_to_cashier ? "bg-teal-600" : "bg-slate-300"
                              }`}
                              title={
                                tx.show_to_cashier
                                  ? "Tampil di role Kasir (Klik untuk menyembunyikan)"
                                  : "Sembunyi di role Kasir (Klik untuk menampilkan)"
                              }>
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  tx.show_to_cashier
                                    ? "translate-x-5"
                                    : "translate-x-0"
                                }`}
                              />
                            </button>
                          ) : (
                            <span
                              className={`inline-block px-2.5 py-1 rounded text-xs font-semibold ${
                                tx.show_to_cashier
                                  ? "bg-teal-50 text-teal-700 border border-teal-200"
                                  : "bg-slate-100 text-slate-500 border border-slate-200"
                              }`}>
                              {tx.show_to_cashier ? "Ya" : "Tidak"}
                            </span>
                          )}
                        </td>
                      )}

                      {/* Name */}
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {tx.name}
                      </td>

                      {/* Tipe Akun */}
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {tx.category}
                      </td>

                      {/* Metode Pembayaran */}
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {tx.payment_methods_label}
                      </td>

                      {/* Total Saldo */}
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold">
                        {tx.balance}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Showing {startEntry} to {endEntry} of {totalEntries} entries
              </span>
              <SearchableSelect
                options={[
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                ]}
                value={itemsPerPage}
                onChange={(value) => onItemsPerPageChange(value)}
                placeholder="Items per page"
                displayKey="label"
                valueKey="value"
                className="w-20"
                searchable={false}
                size="sm"
              />
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ‹‹
              </button>
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ‹
              </button>

              {Array.from({ length: Math.min(3, maxPages) }, (_, i) => {
                let p = currentPage;
                if (currentPage === 1) p = 1;
                else if (currentPage === maxPages) p = maxPages - 2;
                else p = currentPage - 1;

                if (maxPages < 3) p = 1;

                const pageNum = p + i;
                if (pageNum > maxPages || pageNum < 1) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-teal-600 text-white shadow-sm shadow-teal-600/20"
                        : "bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-100"
                    }`}>
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() =>
                  onPageChange(Math.min(maxPages, currentPage + 1))
                }
                disabled={currentPage === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ›
              </button>
              <button
                onClick={() => onPageChange(maxPages)}
                disabled={currentPage === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ››
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
